---
title: "Long vs Short: Profiting From Rising and Falling Markets"
slug: long-vs-short
shortTitle: "Long vs Short"
description: "Going long means you gain when a price rises; going short means you gain when it falls. See how both work on the platform, the math in USDT, and the risks that differ."
level: beginner
category: trading
course: beginner
order: 5
topics: [cfds, trading-strategies]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/long-vs-short.svg
faqs:
  - q: "What does it mean to go short?"
    a: "Going short means opening a position that gains when the price falls. On the platform you do it by pressing Sell; the position opens at the Bid and closes later at the Ask."
  - q: "Do I need to own an asset to short it?"
    a: "Not with a CFD. In traditional markets short sellers borrow shares before selling them, but a CFD is a contract on the price, so opening a sell position needs only margin, just like a buy."
  - q: "Is shorting riskier than buying?"
    a: "The math is the same in reverse, but the worst case differs. A price can only fall to zero, while in theory it can rise without limit, and sudden rallies can hurt a short quickly. A stop loss caps the planned loss in either direction, although it can slip in fast markets."
  - q: "How do I close a long position?"
    a: "Use Close next to the position in Open Positions, or let your take profit or stop loss close it. Pressing Sell on the same market opens a separate sell position instead, because the platform allows a buy and a sell to be held at the same time."
  - q: "Can I be long and short on the same market at once?"
    a: "Yes, the platform allows hedging. Matched buy and sell volume offsets for margin purposes, but you still pay the spread, fees and any swap on both positions."
---

When you buy something in everyday life, you hope it holds or gains value. Trading works that way too, but with CFDs you can also do the opposite: open a position that gains when the price **falls**. Traders call the first "going long" and the second "going short".

Being able to trade both directions is one of the main differences between trading CFDs and buying assets outright. This lesson explains how each direction works on {{BRAND}}, walks through the math in USDT, and covers the ways the risks are not quite symmetrical.

<CTA />

<KeyTakeaways>

- **Long means you gain if the price rises.** You open with **Buy** at the Ask and close later by selling at the Bid.
- **Short means you gain if the price falls.** You open with **Sell** at the Bid and close later by buying back at the Ask.
- **The math is a mirror image.** Profit or loss is the price change times your size; only the sign flips between long and short.
- **Shorts have their own risks.** A price can rise without limit, holding costs can differ by direction, and sharp rallies can hurt quickly.

</KeyTakeaways>

## What "long" and "short" mean

The words come from traditional markets. Being **long** meant owning an asset, so you gained if its price went up. Being **short** meant owing an asset you had sold without owning it, so you gained if you could buy it back later for less.

To short a share the traditional way, a trader borrows it from a broker, sells it, buys it back later and returns it, paying a borrowing fee along the way. A CFD removes that step. Because a CFD is a contract on the price (see [What Is a CFD?](/academy/article/what-is-a-cfd)), opening a sell position is as simple as opening a buy. There is nothing to borrow.

## How each direction works on {{BRAND}}

The order panel on every trade screen has two direction buttons. **Buy** opens a long position and **Sell** opens a short one. Each has its own entry and exit price because every market shows two prices: the **Ask**, which is what you pay to buy, and the **Bid**, which is what you receive when you sell.

<CompareTable caption="Long and short on {{BRAND}}">

| | Long | Short |
| --- | --- | --- |
| Button that opens it | Buy | Sell |
| Opens at | The Ask | The Bid |
| Closes at | The Bid | The Ask |
| You gain when | The price rises | The price falls |
| Take profit goes | Above your entry | Below your entry |
| Stop loss goes | Below your entry | Above your entry |
| Worst case in theory | The price falls to zero | No upper limit |

</CompareTable>

Because you open on one side of the spread and close on the other, both longs and shorts start slightly negative. [Bid, Ask, Spread and Why They Matter](/academy/article/bid-ask-spread) explains why.

<Screenshot name="order-panel-buy-sell" alt="The order panel with the Buy and Sell direction buttons" caption="Buy opens a long at the Ask; Sell opens a short at the Bid." />

## The math, both ways

The formulas are simple:

- **Long:** profit or loss = (closing price − opening price) × size
- **Short:** profit or loss = (opening price − closing price) × size

<Example title="A long ETH/USDT position (illustrative prices)">

You buy 0.5 ETH at 3,000, a position worth 0.5 × 3,000 = **1,500 USDT**.

- ETH rises to 3,150: (3,150 − 3,000) × 0.5 = **+75 USDT**.
- ETH falls to 2,880: (2,880 − 3,000) × 0.5 = **−60 USDT**.

</Example>

<Example title="A short GOLD/USDT position (illustrative prices)">

You sell 2 ounces of gold (0.02 lots, since 1 lot is 100 ounces) at 2,400, a position worth 2 × 2,400 = **4,800 USDT**.

- Gold falls to 2,370: (2,400 − 2,370) × 2 = **+60 USDT**.
- Gold rises to 2,425: (2,400 − 2,425) × 2 = **−50 USDT**.

</Example>

In both cases, the order panel's **Value of $1 move** row tells you the same thing in advance: 0.5 USDT per dollar for the ETH position and 2 USDT per dollar for the gold position, whichever direction you choose. All figures are before the spread, the trade fee ({{TRADE_FEE}}) and any swap.

## Why the risks are not symmetrical

On paper, a short is just a long in reverse. In practice there are three differences worth knowing.

### The worst case is different

A price cannot fall below zero, so a long position has a floor, even if a very painful one. A price has no ceiling, so in theory a short position's loss has no limit. Leverage makes either case serious, because losses are calculated on the full position size rather than on your margin.

### Rallies can be violent

When many traders are short and the price starts rising, some of them close by buying back. That buying pushes the price higher, which forces more of them out. This chain reaction is called a **short squeeze**, and it can move individual stocks and crypto very quickly.

### Holding costs can differ

On markets that charge a **swap**, a daily holding cost applied at {{SWAP_HOUR}}, the long and short rates are set separately. One direction may cost more than the other, and on some markets one side may even receive a small credit. [Trading Costs, Swaps and Fees](/academy/article/trading-costs-swaps-fees) explains how to check this.

<Warning>

A short position can lose more than you expect if the price jumps sharply, for example after news or when a market reopens. Set a stop loss above your entry on every short, and remember that a stop fills at the next available price, which can be worse than the level you chose.

</Warning>

## Choosing a direction

Neither direction is better. The right one is whichever matches your analysis, and you should be able to explain it before you click. Many beginners only ever buy, because buying feels natural, and miss the fact that a sell is the same tool pointed the other way.

Before opening either, answer the same three questions:

1. **What is my reason?** A view you could write down, not a feeling.
2. **Where am I wrong?** For a long, a price below your entry; for a short, a price above it. That becomes your stop loss.
3. **How much is that in USDT?** Price distance to the stop × size. If the number is more than you are willing to lose, make the position smaller.

<ProTip>

To close a long, do not press **Sell**. On {{BRAND}}, Sell opens a new short position alongside your long. Close a position with **Close** in the Open Positions tab, or let its TP or SL close it for you.

</ProTip>

{/* TODO: verify with platform — that pressing Sell while holding a buy on the same market opens a separate hedged sell position rather than reducing the buy. */}

## Holding a long and a short at the same time

{{BRAND}} allows **hedging**: holding a buy and a sell on the same market at once. When you do, the order panel shows **Margin required (hedged)**, because matched buy and sell volume offset each other for margin purposes.

A full hedge freezes your result: whatever the price does, gains on one side cancel losses on the other. It does not remove costs, though. You still pay the spread and fee on both positions, plus any swap on each, and the moment you close one side you are exposed again. Hedging is a tool with specific uses, covered in [Hedging and Correlation](/academy/article/hedging-and-correlation), not a way to make a losing trade disappear.

## Conclusion

Going long means you gain when the price rises; going short means you gain when it falls. On {{BRAND}} that is the difference between the **Buy** and **Sell** buttons, and the profit and loss math is the same in mirror image. What changes is the shape of the risk: a short has no theoretical ceiling, can be caught in a squeeze and may carry a different swap. Pick your direction from your analysis, set a stop on the correct side, and close positions with **Close** rather than by trading the other way.
